import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trees, Leaf, Bird, Droplets, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const Forest = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <Trees size={32} />,
      title: 'Sal & Teak Forests',
      description:
        'Dense sal forests dominate the landscape, forming the ecological backbone of Pilibhit Tiger Reserve.',
    },
    {
      icon: <Droplets size={32} />,
      title: 'Rivers & Wetlands',
      description:
        'Seasonal rivers and wetlands sustain wildlife year-round and support rich biodiversity.',
    },
    {
      icon: <Bird size={32} />,
      title: 'Birdlife',
      description:
        'The reserve is home to resident and migratory birds, especially during winter months.',
    },
    {
      icon: <Mountain size={32} />,
      title: 'Terai Grasslands',
      description:
        'Open grasslands provide grazing grounds for deer and movement corridors for predators.',
    },
    {
      icon: <Leaf size={32} />,
      title: 'Protected Ecosystem',
      description:
        'Part of the Terai Arc Landscape, connecting wildlife habitats across India and Nepal.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center">
        <img
          src="https://images.unsplash.com/photo-1448375240586-882707db888b"
          alt="Pilibhit Forest"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-4"
        >
          <p className="overline mb-4">THE FOREST</p>
          <h1
            className="text-5xl md:text-7xl font-bold"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Pilibhit Tiger Reserve
          </h1>
        </motion.div>
      </section>

      {/* Intro */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg leading-relaxed text-[#64748B]">
            Nestled along the Indo–Nepal border, Pilibhit Tiger Reserve spans the districts of Pilibhit, Lakhimpur Kheri, and Bahraich in Uttar Pradesh. Declared a Tiger Reserve in 2008, it is one of India’s most pristine Terai landscapes—where vast sal forests, grasslands, and wetlands flow seamlessly into one another.
            Part of the Terai Arc Landscape, the reserve supports a tiger population uniquely adapted to this region, a distinction recognized by the Wildlife Institute of India. Its rich ecosystem shelters over 127 species of mammals, 300+ bird species, and thousands of flowering plants.
            From elusive Bengal tigers and swamp deer to vibrant birdlife and serene water bodies, Pilibhit is a forest of quiet power and rare balance. The Chuka Interpretation Zone, overlooking expansive wetlands, offers a peaceful gateway into this living wilderness: untamed, timeless, and deeply restorative.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl"
              style={{ background: '#F9F8F6' }}
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 bg-white text-[#1A3C34]">
                {f.icon}
              </div>
              <h3
                className="text-xl font-bold mb-2"
                style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
              >
                {f.title}
              </h3>
              <p className="text-[#64748B]">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Images */}
      <section className="section-padding">
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          <img
            className="rounded-2xl"
            src="https://res.cloudinary.com/dwwjvrlvg/image/upload/v1768661227/IMG_2894_nqum80.jpg"
            alt="Forest Path"
          />
          <img
            className="rounded-2xl"
            src="https://res.cloudinary.com/dwwjvrlvg/image/upload/v1768661232/1000160019_yixsit.jpg"
            alt="property of Anil Vohra"
          />
          <img
            className="rounded-2xl"
            src="https://res.cloudinary.com/dwwjvrlvg/image/upload/v1768661517/Screenshot_2026-01-17_at_8.21.50_PM_ldjubw.png"
            alt="Property of Namrata Vohra"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Ready to Experience the Forest?
            </h2>
            <p className="text-lg mb-8 text-[#64748B]">
              Book your stay and let nature restore your soul.
            </p>
            <Link to="/rooms">
              <button className="btn-primary">View Our Rooms</button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#1A3C34] text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3
            className="text-3xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            The BaghHome
          </h3>
          <p className="text-white/70 mb-6">Your Escape Into Nature</p>
          <p className="text-white/50 text-sm">
            © 2025 The BaghHome. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Forest;

